/**
 * Dedicated search page — uses useSearch hook for debounced full-text search.
 * Results are grouped by location with query-term highlighting.
 * Shows recent searches when query is empty.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SearchBar } from '../components/SearchBar';
import { SearchResultCard } from '../components/SearchResultCard';
import { Spinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { useSearch } from '../hooks/useSearch';
import type { SearchResult } from '../hooks/useSearch';

interface LocationGroup {
  location: string;
  results: SearchResult[];
}

function groupByLocation(results: SearchResult[]): LocationGroup[] {
  const map = new Map<string, SearchResult[]>();

  for (const r of results) {
    const loc = r.box.location || 'No location';
    const group = map.get(loc);
    if (group) {
      group.push(r);
    } else {
      map.set(loc, [r]);
    }
  }

  return Array.from(map.entries())
    .map(([location, items]) => ({ location, results: items }))
    .sort((a, b) => {
      // "No location" always last
      if (a.location === 'No location') return 1;
      if (b.location === 'No location') return -1;
      return a.location.localeCompare(b.location);
    });
}

export function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') ?? '';

  const {
    query,
    setQuery,
    results,
    loading,
    error,
    recentSearches,
    clearRecentSearches,
    removeRecentSearch,
  } = useSearch();

  // Sync URL query param → hook state
  useEffect(() => {
    if (urlQuery !== query) {
      setQuery(urlQuery);
    }
    // Only run when urlQuery changes (not query — avoids loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, setQuery]);

  function handleChange(q: string) {
    if (q) {
      setSearchParams({ q }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
    setQuery(q);
  }

  function handleResultClick(boxId: string) {
    navigate(`/boxes/${boxId}`);
  }

  function handleRecentClick(q: string) {
    setSearchParams({ q }, { replace: true });
    setQuery(q);
  }

  const groups = useMemo(() => groupByLocation(results), [results]);
  const showRecent = !query.trim() && recentSearches.length > 0;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-text-primary">Search</h1>

      <SearchBar
        value={query}
        onChange={handleChange}
        autoFocus
        placeholder="Search boxes, items, locations..."
      />

      {/* Recent searches */}
      {showRecent && (
        <div data-testid="recent-searches">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
              Recent Searches
            </h2>
            <button
              type="button"
              onClick={clearRecentSearches}
              className="text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentSearches.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => handleRecentClick(q)}
                className="group flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-text-primary hover:border-accent/50 transition-colors"
              >
                <svg
                  className="h-3.5 w-3.5 text-text-secondary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {q}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRecentSearch(q);
                  }}
                  className="ml-0.5 text-text-secondary/50 hover:text-text-primary transition-colors"
                  aria-label={`Remove "${q}" from recent searches`}
                >
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : query.trim().length > 0 && query.trim().length < 2 ? (
        <p className="text-sm text-text-secondary text-center py-8">
          Type at least 2 characters to search.
        </p>
      ) : query.trim().length >= 2 && results.length === 0 ? (
        <EmptyState
          title="No results found"
          description={`Nothing matching "${query}". Try a different search term.`}
        />
      ) : groups.length > 0 ? (
        <div className="space-y-6" data-testid="search-results">
          <p className="text-sm text-text-secondary">
            {results.length} result{results.length !== 1 ? 's' : ''}
          </p>

          {groups.map((group) => (
            <div key={group.location}>
              <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {group.location}
                <span className="text-text-secondary/50">({group.results.length})</span>
              </h2>
              <div className="space-y-2">
                {group.results.map((result) => (
                  <SearchResultCard
                    key={result.box.id}
                    result={result}
                    query={query}
                    onClick={handleResultClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : !showRecent && !query.trim() ? (
        <EmptyState
          icon={
            <svg
              className="h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          }
          title="Search your boxes"
          description="Type at least 2 characters to search across all boxes and items."
        />
      ) : null}
    </div>
  );
}
